// Rol de pagos mensual: generar borrador, recalcular, cerrar; e historial.
// Nómina → Rol de Pagos. El motor y las reglas legales viven en la BD
// (nom_rol_generar / nom_rol_cerrar — script 162).
import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, BookOpen, ChevronDown, ChevronRight, Loader2, Lock, Mail, RefreshCw, RotateCcw, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { MESES, TIPOS_ROL, fecha, fieldCls, money, nomRpc, useDialogoEnLinea, type EnvioRol, type RolDetalleData, type RolEmpleadoRow, type RolEnviosData, type RolHeader } from './shared';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';

const HOY = new Date();

export function EstadoRol({ estado }: { estado: RolHeader['estado'] }) {
  const cls = estado === 'borrador' ? 'bg-pendiente-soft text-pendiente'
    : estado === 'cerrado' ? 'bg-estable-soft text-estable' : 'bg-completado-soft text-completado';
  return <span className={`px-2 py-0.5 rounded-full text-xs ${cls}`}>{estado}</span>;
}

const ESTADO_ENVIO: Record<string, { label: string; cls: string }> = {
  pendiente: { label: 'En cola', cls: 'bg-pendiente-soft text-pendiente' },
  enviando: { label: 'Enviando…', cls: 'bg-pendiente-soft text-pendiente' },
  enviado: { label: 'Enviado', cls: 'bg-estable-soft text-estable' },
  error: { label: 'Error', cls: 'bg-urgente-soft text-urgente' },
};

function FilaEmpleado({ e, envio, onEnviar }: { e: RolEmpleadoRow; envio?: EnvioRol | null; onEnviar?: (id: string) => void }) {
  const [abierto, setAbierto] = useState(false);
  return (
    <Fragment>
      <tr className="border-b last:border-0 hover:bg-muted/40 cursor-pointer" onClick={() => setAbierto(!abierto)}
          role="button" tabIndex={0} onKeyDown={(ev) => { if (ev.key === 'Enter') setAbierto(!abierto); }}>
        <td className="py-2 pr-2">{abierto ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
        <td className="py-2 pr-3 font-mono">{e.empleado_codigo}</td>
        <td className="py-2 pr-3">{e.razon_social}<div className="text-xs text-muted-foreground font-mono">{e.identificacion}</div></td>
        <td className="py-2 pr-3">{e.cargo ?? '—'}</td>
        <td className="py-2 pr-3 text-right font-mono tabular-nums">{e.dias}</td>
        <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(e.ingresos)}</td>
        <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(e.egresos)}</td>
        <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(e.neto)}</td>
        {onEnviar && (
          <td className="py-2 pr-2 text-right whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
            {envio && (
              <span className={`px-2 py-0.5 rounded-full text-xs mr-1 ${ESTADO_ENVIO[envio.estado].cls}`}
                    title={envio.estado === 'error' ? (envio.error ?? '') : envio.email}>
                {ESTADO_ENVIO[envio.estado].label}
              </span>
            )}
            <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted"
              title={envio?.estado === 'enviado' ? 'Reenviar por correo' : 'Enviar por correo'}
              onClick={() => onEnviar(e.id)}>
              <Send className="w-4 h-4" />
            </button>
          </td>
        )}
      </tr>
      {abierto && (
        <tr className="bg-muted/20">
          <td colSpan={onEnviar ? 9 : 8} className="py-2 px-8">
            <table className="w-full text-xs">
              <tbody>
                {e.rubros.map((r, i) => (
                  <tr key={i} className="border-b border-border/40 last:border-0">
                    <td className="py-1 pr-3 font-mono">{r.rubro_codigo}</td>
                    <td className="py-1 pr-3">{r.descripcion}</td>
                    <td className="py-1 pr-3 text-right text-muted-foreground">{r.cantidad != null ? Number(r.cantidad) : ''}</td>
                    <td className={`py-1 text-right font-mono tabular-nums ${r.tipo === 'egreso' ? 'text-urgente' : ''}`}>
                      {r.tipo === 'egreso' ? '−' : ''}{money(r.valor)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

export function RolDetalleView({ detalle }: { detalle: RolDetalleData }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [q, setQ] = useState('');
  const rol = detalle.rol;
  const pagado = rol?.estado === 'pagado';
  const { data: enviosData } = useQuery<RolEnviosData>({
    queryKey: ['nom-rol-envios', rol?.id],
    // El argumento se llama `_rol_id`, no `_id`. PostgREST resuelve la función
    // por el nombre del argumento: con `_id` devuelve 404, no un error de datos.
    queryFn: () => nomRpc<RolEnviosData>('nom_rol_envios', { _rol_id: rol!.id }),
    enabled: !!rol && pagado,
    refetchInterval: (query) => ((query.state.data?.resumen.pendientes ?? 0) > 0 ? 4000 : false),
  });
  const invalidarEnvios = () => { void qc.invalidateQueries({ queryKey: ['nom-rol-envios'] }); };
  const enviarMasivo = useMutation({
    mutationFn: () => nomRpc<{ encolados: number; reactivados: number; sin_email: number; ya_enviados: number }>('nom_rol_enviar_masivo', { _rol_id: rol!.id }),
    onSuccess: (r) => {
      toast.success(`Encolados ${r.encolados + r.reactivados} correos (${r.ya_enviados} ya enviados, ${r.sin_email} sin email)`);
      invalidarEnvios();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const enviarUno = useMutation({
    mutationFn: (rolEmpleadoId: string) => nomRpc('nom_rol_enviar_uno', { _rol_empleado_id: rolEmpleadoId }),
    onSuccess: () => { toast.success('Correo encolado'); invalidarEnvios(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!rol) return null;
  const t = q.trim().toLowerCase();
  const empleados = t
    ? detalle.empleados.filter((e) => e.razon_social.toLowerCase().includes(t) || e.identificacion.includes(t))
    : detalle.empleados;
  const rs = enviosData?.resumen;

  const headersExp = ['N°', 'Código', 'Empleado', 'Cargo', 'Días', 'Ingresos', 'Egresos', 'Neto'];
  const MONEYCOLS = [5, 6, 7];
  const filasExport: Celda[][] = [
    ...empleados.map((e, i): Celda[] => [
      i + 1, e.empleado_codigo, e.razon_social, e.cargo ?? '—',
      Number(e.dias), Number(e.ingresos), Number(e.egresos), Number(e.neto),
    ]),
    ['', '', '', 'TOTAL',
      empleados.reduce((s, e) => s + Number(e.dias), 0),
      empleados.reduce((s, e) => s + Number(e.ingresos), 0),
      empleados.reduce((s, e) => s + Number(e.egresos), 0),
      empleados.reduce((s, e) => s + Number(e.neto), 0)],
  ];

  return (
    <div className="space-y-4">
      {dlg.ui}
      {pagado && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" isLoading={enviarMasivo.isPending}
            onClick={async () => { if (await dlg.confirmar('¿Enviar el rol de pago por correo a todos?', { detalle: 'A todos los empleados con email (PDF protegido con su cédula).', aceptar: 'Enviar' })) enviarMasivo.mutate(); }}>
            <Mail className="w-4 h-4 mr-1" /> Enviar por correo a todos
          </Button>
          {rs && (
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-1 rounded-full bg-estable-soft text-estable">Enviados: {rs.enviados}</span>
              {rs.pendientes > 0 && <span className="px-2 py-1 rounded-full bg-pendiente-soft text-pendiente">En cola: {rs.pendientes}</span>}
              {rs.errores > 0 && <span className="px-2 py-1 rounded-full bg-urgente-soft text-urgente">Errores: {rs.errores}</span>}
              {rs.sin_email > 0 && <span className="px-2 py-1 rounded-full bg-muted text-muted-foreground">Sin email: {rs.sin_email}</span>}
            </div>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { k: 'Empleados', v: String(rol.n_empleados) },
          { k: 'Ingresos', v: money(rol.total_ingresos) },
          { k: 'Egresos', v: money(rol.total_egresos) },
          { k: 'Neto a pagar', v: money(rol.total_neto) },
        ].map((x) => (
          <Card key={x.k}><CardContent className="py-3">
            <div className="text-xs text-muted-foreground">{x.k}</div>
            <div className="text-lg font-semibold font-mono tabular-nums">{x.v}</div>
          </CardContent></Card>
        ))}
      </div>
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Detalle por empleado ({empleados.length})</CardTitle>
          <div className="flex items-center gap-2">
            <input className={`${fieldCls} w-64`} placeholder="Filtrar nombre o cédula…" value={q} onChange={(e) => setQ(e.target.value)} />
            <ExportarBotones
              nombre={`rol-detalle-${fechaLocal()}`} titulo="Rol de pagos"
              headers={headersExp} filas={filasExport} colsDerecha={[0, 4, ...MONEYCOLS]}
              fmtPDF={(v, c) => (MONEYCOLS.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
              disabled={empleados.length === 0} />
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-2 w-6"></th><th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Empleado</th>
              <th className="py-2 pr-3">Cargo</th><th className="py-2 pr-3 text-right">Días</th>
              <th className="py-2 pr-3 text-right">Ingresos</th><th className="py-2 pr-3 text-right">Egresos</th><th className="py-2 pr-3 text-right">Neto</th>
              {pagado && <th className="py-2 pr-2 text-right">Correo</th>}
            </tr></thead>
            <tbody>{empleados.map((e) => (
              <FilaEmpleado key={e.id} e={e}
                envio={pagado ? enviosData?.envios[e.id] ?? null : undefined}
                onEnviar={pagado ? (id) => enviarUno.mutate(id) : undefined} />
            ))}</tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

export function GenerarRol() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [anio, setAnio] = useState(HOY.getFullYear());
  const [mes, setMes] = useState(HOY.getMonth() + 1);
  const [tipo, setTipo] = useState('general');

  const { data: roles = [] } = useQuery<RolHeader[]>({
    queryKey: ['nom-roles'],
    queryFn: () => nomRpc<RolHeader[]>('nom_roles', {}),
    staleTime: 0,
  });
  const rolesPeriodo = roles.filter((r) => r.anio === anio && r.mes === mes);
  const rolPeriodo = rolesPeriodo.find((r) => r.tipo === tipo) ?? null;

  const { data: detalle, isFetching } = useQuery<RolDetalleData>({
    queryKey: ['nom-rol', rolPeriodo?.id],
    queryFn: () => nomRpc<RolDetalleData>('nom_rol', { _id: rolPeriodo!.id }),
    enabled: !!rolPeriodo,
    staleTime: 0,
  });

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['nom-roles'] });
    void qc.invalidateQueries({ queryKey: ['nom-rol'] });
    void qc.invalidateQueries({ queryKey: ['nom-novedades'] });
    void qc.invalidateQueries({ queryKey: ['nom-anticipos'] });
    void qc.invalidateQueries({ queryKey: ['nom-prestamos'] });
  };
  const generar = useMutation({
    mutationFn: () => nomRpc<RolDetalleData>('nom_rol_generar', { _anio: anio, _mes: mes, _tipo: tipo }),
    onSuccess: () => { toast.success(`Rol ${tipo === 'general' ? '' : tipo + ' '}${MESES[mes - 1]} ${anio} generado (borrador)`); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cerrar = useMutation({
    mutationFn: () => nomRpc<RolDetalleData>('nom_rol_cerrar', { _id: rolPeriodo!.id }),
    onSuccess: () => { toast.success('Rol cerrado: novedades, anticipos y préstamos liquidados'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: () => nomRpc('nom_rol_eliminar', { _id: rolPeriodo!.id }),
    onSuccess: () => { toast.success('Borrador eliminado'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const contabilizar = useMutation({
    mutationFn: () => nomRpc<{ numero: number; reemplazado: boolean }>('nom_rol_contabilizar', { _rol_id: rolPeriodo!.id }),
    onSuccess: (r) => {
      toast.success(`Asiento contable N.º ${r.numero} ${r.reemplazado ? 'regenerado' : 'creado'} (Contabilidad → Asientos)`);
      invalidar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const reabrir = useMutation({
    mutationFn: async () => {
      const v = await dlg.pedir('Motivo de la reapertura', [{ key: 'motivo', label: 'Motivo (queda registrado en la bitácora)', minimo: 1 }]);
      if (v === null) return Promise.reject(new Error('Reapertura cancelada: falta el motivo'));
      return nomRpc<{ novedades_liberadas: number; anticipos_revertidos: number; abonos_prestamo_revertidos: number; asiento_eliminado: boolean }>('nom_rol_reabrir', { _id: rolPeriodo!.id, _motivo: v.motivo.trim() });
    },
    onSuccess: (r) => {
      toast.success(`Rol reabierto (borrador). Revertidos: ${r.anticipos_revertidos} anticipo(s), ${r.abonos_prestamo_revertidos} abono(s) de préstamo${r.asiento_eliminado ? ', asiento eliminado' : ''}.`);
      invalidar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Rol de pagos</h1>
        <div className="flex items-center gap-2">
          <select className={`${fieldCls} w-36`} value={mes} onChange={(e) => setMes(Number(e.target.value))} aria-label="Mes">
            {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <input type="number" className={`${fieldCls} w-24`} value={anio} onChange={(e) => setAnio(Number(e.target.value))} aria-label="Año" />
          <select className={`${fieldCls} w-40`} value={tipo} onChange={(e) => setTipo(e.target.value)} aria-label="Tipo de rol">
            {Object.entries(TIPOS_ROL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {rolPeriodo && <EstadoRol estado={rolPeriodo.estado} />}
        </div>
      </div>

      {rolesPeriodo.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">Roles del período:</span>
          {rolesPeriodo.map((r) => (
            <button key={r.id}
              className={`px-2 py-0.5 rounded-full border ${r.tipo === tipo ? 'bg-primary/10 text-primary border-primary/30' : 'bg-muted text-muted-foreground border-transparent hover:border-border'}`}
              onClick={() => setTipo(r.tipo)}>
              {TIPOS_ROL[r.tipo]} · {r.estado} · {r.n_empleados} emp.
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {!rolPeriodo && (
          <Button isLoading={generar.isPending} onClick={() => generar.mutate()}>Generar rol (borrador)</Button>
        )}
        {rolPeriodo?.estado === 'borrador' && (
          <>
            <Button variant="outline" isLoading={generar.isPending} onClick={() => generar.mutate()}>
              <RefreshCw className="w-4 h-4 mr-1" /> Recalcular
            </Button>
            <Button isLoading={cerrar.isPending} onClick={async () => { if (await dlg.confirmar('¿Cerrar el rol?', { detalle: 'Cerrar el rol liquida novedades, anticipos y cuotas de préstamo.', aceptar: 'Cerrar rol' })) cerrar.mutate(); }}>
              <Lock className="w-4 h-4 mr-1" /> Cerrar rol
            </Button>
            <Button variant="destructive" isLoading={eliminar.isPending} onClick={async () => { if (await dlg.confirmar('¿Eliminar el borrador?', { peligro: true, aceptar: 'Eliminar' })) eliminar.mutate(); }}>
              <Trash2 className="w-4 h-4 mr-1" /> Eliminar borrador
            </Button>
          </>
        )}
        {rolPeriodo && rolPeriodo.estado !== 'borrador' && (
          <>
            <Button variant="outline" isLoading={contabilizar.isPending}
              onClick={async () => {
                const ya = !!detalle?.asiento;
                const ok = await dlg.confirmar(ya ? '¿Regenerar el asiento?' : '¿Contabilizar el rol?', {
                  detalle: ya
                    ? `El rol ya está contabilizado en el asiento N.º ${detalle!.asiento!.numero}. Regenerarlo reemplaza el asiento.`
                    : 'Se creará el asiento contable del rol (requiere el mapeo de cuentas en Contabilidad → Configuración Contable).',
                  aceptar: ya ? 'Regenerar' : 'Contabilizar',
                });
                if (ok) contabilizar.mutate();
              }}>
              <BookOpen className="w-4 h-4 mr-1" /> {detalle?.asiento ? 'Re-contabilizar' : 'Contabilizar'}
            </Button>
            {rolPeriodo.estado === 'cerrado' && (
              <Button variant="outline" isLoading={reabrir.isPending}
                onClick={async () => { if (await dlg.confirmar('¿Reabrir el rol?', { detalle: 'Reabrir devuelve el rol a BORRADOR y revierte el cierre: libera novedades, anticipos vuelven a pendiente, se restauran los saldos de préstamo y se elimina el asiento contable.', aceptar: 'Reabrir' })) reabrir.mutate(); }}>
                <RotateCcw className="w-4 h-4 mr-1" /> Reabrir
              </Button>
            )}
            {detalle?.asiento && (
              <span className="self-center px-2 py-0.5 rounded-full text-xs bg-completado-soft text-completado">
                Asiento N.º {detalle.asiento.numero} ({fecha(detalle.asiento.fecha)})
              </span>
            )}
            <div className="text-sm text-muted-foreground self-center">
              Rol {rolPeriodo.estado}. El pago se gestiona en Rol de Pagos → Pagos y acreditaciones.
            </div>
          </>
        )}
      </div>

      {isFetching && <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
      {detalle && <RolDetalleView detalle={detalle} />}
      {!rolPeriodo && !generar.isPending && (
        <Card><CardContent className="py-10 text-center text-muted-foreground">
          No hay rol {tipo === 'general' ? '' : `${TIPOS_ROL[tipo].toLowerCase()} `}para {MESES[mes - 1]} {anio}. Genera el borrador para calcular la nómina del período.
          {tipo !== 'general' && ' Los roles por tipo solo incluyen empleados con esa frecuencia de pago, y no se pueden mezclar con un rol general en el mismo mes.'}
        </CardContent></Card>
      )}
    </div>
  );
}

export function HistorialRoles() {
  const [selId, setSelId] = useState<string | null>(null);
  const [fTipo, setFTipo] = useState('todos');
  const [fAnio, setFAnio] = useState('todos');
  const [fMes, setFMes] = useState('todos');
  const { data: roles = [], isFetching } = useQuery<RolHeader[]>({
    queryKey: ['nom-roles'],
    queryFn: () => nomRpc<RolHeader[]>('nom_roles', {}),
  });
  const anios = [...new Set(roles.map((r) => r.anio))].sort((a, b) => b - a);
  const filtrados = roles.filter((r) =>
    (fTipo === 'todos' || r.tipo === fTipo)
    && (fAnio === 'todos' || r.anio === Number(fAnio))
    && (fMes === 'todos' || r.mes === Number(fMes)));
  const { data: detalle, isFetching: cargandoDet } = useQuery<RolDetalleData>({
    queryKey: ['nom-rol', selId],
    queryFn: () => nomRpc<RolDetalleData>('nom_rol', { _id: selId }),
    enabled: !!selId,
  });

  if (selId) {
    return (
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setSelId(null)}><ArrowLeft className="w-4 h-4 mr-1" /> Volver</Button>
          {detalle?.rol && (
            <h1 className="text-xl font-semibold">
              Rol {MESES[detalle.rol.mes - 1]} {detalle.rol.anio} <span className="font-mono text-sm text-muted-foreground">{detalle.rol.codigo}</span>
            </h1>
          )}
          {detalle?.rol && <EstadoRol estado={detalle.rol.estado} />}
          {detalle?.asiento && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-completado-soft text-completado">
              Asiento N.º {detalle.asiento.numero}
            </span>
          )}
        </div>
        {cargandoDet && <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
        {detalle && <RolDetalleView detalle={detalle} />}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold">Historial de roles</h1>
      <div className="flex flex-wrap items-center gap-2">
        <select className={`${fieldCls} w-44`} value={fTipo} onChange={(e) => setFTipo(e.target.value)} aria-label="Tipo de rol">
          <option value="todos">Todos los tipos</option>
          {Object.entries(TIPOS_ROL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className={`${fieldCls} w-32`} value={fAnio} onChange={(e) => setFAnio(e.target.value)} aria-label="Año">
          <option value="todos">Todos los años</option>
          {anios.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className={`${fieldCls} w-40`} value={fMes} onChange={(e) => setFMes(e.target.value)} aria-label="Mes">
          <option value="todos">Todos los meses</option>
          {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        {(fTipo !== 'todos' || fAnio !== 'todos' || fMes !== 'todos') && (
          <button className="text-xs text-primary hover:underline" onClick={() => { setFTipo('todos'); setFAnio('todos'); setFMes('todos'); }}>
            Limpiar filtros
          </button>
        )}
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Roles generados ({filtrados.length}{filtrados.length !== roles.length ? ` de ${roles.length}` : ''})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Período</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Estado</th>
              <th className="py-2 pr-3 text-right">Empleados</th><th className="py-2 pr-3 text-right">Ingresos</th>
              <th className="py-2 pr-3 text-right">Egresos</th><th className="py-2 pr-3 text-right">Neto</th>
            </tr></thead>
            <tbody>
              {filtrados.length === 0 && !isFetching && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">{roles.length === 0 ? 'Aún no se han generado roles' : 'Ningún rol coincide con los filtros'}</td></tr>}
              {filtrados.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer" onClick={() => setSelId(r.id)}
                    role="button" tabIndex={0} onKeyDown={(ev) => { if (ev.key === 'Enter') setSelId(r.id); }}>
                  <td className="py-2 pr-3 font-mono">{r.codigo}</td>
                  <td className="py-2 pr-3">{MESES[r.mes - 1]} {r.anio}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${r.tipo !== 'general' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      {TIPOS_ROL[r.tipo] ?? r.tipo}
                    </span>
                  </td>
                  <td className="py-2 pr-3"><EstadoRol estado={r.estado} /></td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{r.n_empleados}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.total_ingresos)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.total_egresos)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(r.total_neto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
